/**
 * GameAudio - Game-specific audio handling
 * Wraps the engine AudioManager with game sounds
 */

import { AudioManager } from '@/engine';

// All game sound effects
export enum SFX {
  // Weapons
  PulsePistolShoot = 'weapon_pulse_pistol_shoot',
  PlasmaRifleShoot = 'weapon_plasma_rifle_shoot',
  ScatterGunShoot = 'weapon_scatter_gun_shoot',
  FusionCannonShoot = 'weapon_fusion_cannon_shoot',
  WeaponReload = 'weapon_reload',
  WeaponEmpty = 'weapon_empty',
  WeaponSwitch = 'weapon_switch',
  
  // Player
  FootstepMetal = 'player_footstep',
  PlayerDamage = 'player_damage',
  PlayerDeath = 'player_death',
  PlayerRespawn = 'player_respawn',
  
  // Combat
  BulletImpactWall = 'combat_impact_wall',
  BulletImpactPlayer = 'combat_impact_player',
  Headshot = 'combat_headshot',
  KillConfirmed = 'combat_kill',
  
  // Abilities
  AbilityCast = 'ability_cast',
  Explosion = 'ability_explosion',
  Teleport = 'ability_teleport',
  
  // Game State
  RoundStart = 'game_round_start',
  RoundEnd = 'game_round_end',
  MatchWin = 'game_match_win',
  MatchLose = 'game_match_lose',
  CountdownTick = 'game_countdown_tick',
  CountdownGo = 'game_countdown_go',
  AirplaneFlyby = 'game_airplane_flyby',
  ParachuteDrop = 'game_parachute_drop',
  WeaponPickup = 'game_weapon_pickup',
  HealthPickup = 'game_health_pickup',
  ItemPickup = 'game_item_pickup',
  MissileIncoming = 'game_missile_incoming',
  RocketLaunch = 'game_rocket_launch',
  MissileExplosion = 'game_missile_explosion',
  
  // UI
  UIClick = 'ui_click',
  UIHover = 'ui_hover',
  UISuccess = 'ui_success',
  UIError = 'ui_error',
}

// Music tracks
export enum Music {
  MenuTheme = 'music_menu',
  GameplayLoop = 'music_gameplay',
  VictoryTheme = 'music_victory',
  DefeatTheme = 'music_defeat',
}

// Sound file mappings
const SOUND_FILES: Record<string, string> = {
  // Weapons
  [SFX.PulsePistolShoot]: './assets/audio/weapons/pulse_pistol_shoot.mp3',
  [SFX.PlasmaRifleShoot]: './assets/audio/weapons/plasma_rifle_shoot.mp3',
  [SFX.ScatterGunShoot]: './assets/audio/weapons/scatter_gun_shoot.mp3',
  [SFX.FusionCannonShoot]: './assets/audio/weapons/fusion_cannon_shoot.mp3',
  [SFX.WeaponReload]: './assets/audio/weapons/reload.mp3',
  [SFX.WeaponEmpty]: './assets/audio/weapons/empty_click.mp3',
  [SFX.WeaponSwitch]: './assets/audio/weapons/weapon_switch.mp3',
  
  // Player
  [SFX.FootstepMetal]: './assets/audio/player/footstep_metal.mp3',
  [SFX.PlayerDamage]: './assets/audio/player/damage.mp3',
  [SFX.PlayerDeath]: './assets/audio/player/death.mp3',
  [SFX.PlayerRespawn]: './assets/audio/player/respawn.mp3',
  
  // Combat
  [SFX.BulletImpactWall]: './assets/audio/combat/impact_wall.mp3',
  [SFX.BulletImpactPlayer]: './assets/audio/combat/impact_player.mp3',
  [SFX.Headshot]: './assets/audio/combat/headshot.mp3',
  [SFX.KillConfirmed]: './assets/audio/combat/kill_confirmed.mp3',
  
  // Abilities
  [SFX.AbilityCast]: './assets/audio/abilities/cast.mp3',
  [SFX.Explosion]: './assets/audio/abilities/explosion.mp3',
  [SFX.Teleport]: './assets/audio/abilities/teleport.mp3',
  
  // Game State
  [SFX.RoundStart]: './assets/audio/game/round_start.mp3',
  [SFX.RoundEnd]: './assets/audio/game/round_end.mp3',
  [SFX.MatchWin]: './assets/audio/game/match_win.mp3',
  [SFX.MatchLose]: './assets/audio/game/match_lose.mp3',
  [SFX.CountdownTick]: './assets/audio/game/countdown_tick.mp3',
  [SFX.CountdownGo]: './assets/audio/game/countdown_go.mp3',
  [SFX.AirplaneFlyby]: './assets/audio/game/airplane-flyby.mp3',
  [SFX.ParachuteDrop]: './assets/audio/game/parachute-drop.mp3',
  [SFX.MissileIncoming]: './assets/audio/game/missile-incoming.mp3',
  [SFX.RocketLaunch]: './assets/audio/game/rocket-launch.mp3',
  [SFX.MissileExplosion]: './assets/audio/game/missile-explosion.mp3',
  [SFX.WeaponPickup]: './assets/audio/game/weapon-equipped.mp3',
  [SFX.HealthPickup]: './assets/audio/game/health-pickup.mp3',
  [SFX.ItemPickup]: './assets/audio/game/item-pickup.mp3',
  
  // UI
  [SFX.UIClick]: './assets/audio/ui/click.mp3',
  [SFX.UIHover]: './assets/audio/ui/hover.mp3',
  [SFX.UISuccess]: './assets/audio/ui/success.mp3',
  [SFX.UIError]: './assets/audio/ui/error.mp3',
  
  // Music
  [Music.MenuTheme]: './assets/audio/music/menu_theme.mp3',
  [Music.GameplayLoop]: './assets/audio/music/gameplay_loop.mp3',
  [Music.VictoryTheme]: './assets/audio/music/victory_theme.mp3',
  [Music.DefeatTheme]: './assets/audio/music/defeat_theme.mp3',
};

// Sound categories for volume control
export enum AudioCategory {
  Master = 'master',
  Music = 'music',
  SFX = 'sfx',
  UI = 'ui',
  Voice = 'voice',
}

// Map sounds to their categories
const SOUND_CATEGORIES: Record<string, AudioCategory> = {
  // Weapons -> SFX
  [SFX.PulsePistolShoot]: AudioCategory.SFX,
  [SFX.PlasmaRifleShoot]: AudioCategory.SFX,
  [SFX.ScatterGunShoot]: AudioCategory.SFX,
  [SFX.FusionCannonShoot]: AudioCategory.SFX,
  [SFX.WeaponReload]: AudioCategory.SFX,
  [SFX.WeaponEmpty]: AudioCategory.SFX,
  [SFX.WeaponSwitch]: AudioCategory.SFX,
  // Player -> SFX
  [SFX.FootstepMetal]: AudioCategory.SFX,
  [SFX.PlayerDamage]: AudioCategory.SFX,
  [SFX.PlayerDeath]: AudioCategory.SFX,
  [SFX.PlayerRespawn]: AudioCategory.SFX,
  // Combat -> SFX
  [SFX.BulletImpactWall]: AudioCategory.SFX,
  [SFX.BulletImpactPlayer]: AudioCategory.SFX,
  [SFX.Headshot]: AudioCategory.SFX,
  [SFX.KillConfirmed]: AudioCategory.SFX,
  // Abilities -> SFX
  [SFX.AbilityCast]: AudioCategory.SFX,
  [SFX.Explosion]: AudioCategory.SFX,
  [SFX.Teleport]: AudioCategory.SFX,
  // Game State -> Voice (announcer-style)
  [SFX.RoundStart]: AudioCategory.Voice,
  [SFX.RoundEnd]: AudioCategory.Voice,
  [SFX.MatchWin]: AudioCategory.Voice,
  [SFX.MatchLose]: AudioCategory.Voice,
  [SFX.CountdownTick]: AudioCategory.Voice,
  [SFX.CountdownGo]: AudioCategory.Voice,
  // UI -> UI
  [SFX.UIClick]: AudioCategory.UI,
  [SFX.UIHover]: AudioCategory.UI,
  [SFX.UISuccess]: AudioCategory.UI,
  [SFX.UIError]: AudioCategory.UI,
  // Music -> Music
  [Music.MenuTheme]: AudioCategory.Music,
  [Music.GameplayLoop]: AudioCategory.Music,
  [Music.VictoryTheme]: AudioCategory.Music,
  [Music.DefeatTheme]: AudioCategory.Music,
};

// Default volumes for specific sounds (relative to category)
const SOUND_VOLUMES: Partial<Record<string, number>> = {
  [SFX.FootstepMetal]: 0.3,
  [SFX.UIHover]: 0.4,
  [SFX.BulletImpactWall]: 0.5,
};

// Audio settings interface
export interface AudioSettings {
  masterVolume: number;    // 0-100
  musicVolume: number;     // 0-100
  sfxVolume: number;       // 0-100
  uiVolume: number;        // 0-100
  voiceVolume: number;     // 0-100
  masterEnabled: boolean;
  musicEnabled: boolean;
  sfxEnabled: boolean;
  uiEnabled: boolean;
  voiceEnabled: boolean;
}

const DEFAULT_AUDIO_SETTINGS: AudioSettings = {
  masterVolume: 80,
  musicVolume: 50,
  sfxVolume: 80,
  uiVolume: 70,
  voiceVolume: 100,
  masterEnabled: true,
  musicEnabled: true,
  sfxEnabled: true,
  uiEnabled: true,
  voiceEnabled: true,
};

export class GameAudio {
  private audioManager: AudioManager;
  private loadedSounds: Set<string> = new Set();
  private settings: AudioSettings;
  
  constructor() {
    this.audioManager = new AudioManager();
    this.settings = { ...DEFAULT_AUDIO_SETTINGS };
    this.loadSettings();
  }
  
  /**
   * Load audio settings from localStorage
   */
  private loadSettings(): void {
    try {
      const saved = localStorage.getItem('cybergrid_audioSettings');
      if (saved) {
        this.settings = { ...DEFAULT_AUDIO_SETTINGS, ...JSON.parse(saved) };
      }
    } catch {
      // Use defaults
    }
  }
  
  /**
   * Save audio settings to localStorage
   */
  saveSettings(): void {
    try {
      localStorage.setItem('cybergrid_audioSettings', JSON.stringify(this.settings));
    } catch {
      // Ignore save errors
    }
  }
  
  /**
   * Get current audio settings
   */
  getSettings(): AudioSettings {
    return { ...this.settings };
  }
  
  /**
   * Update audio settings
   */
  updateSettings(newSettings: Partial<AudioSettings>): void {
    this.settings = { ...this.settings, ...newSettings };
    this.applySettings();
    this.saveSettings();
  }
  
  /**
   * Apply current settings to audio manager
   */
  private applySettings(): void {
    // Master volume affects everything
    this.audioManager.setMasterVolume(this.settings.masterEnabled ? this.settings.masterVolume / 100 : 0);
    this.audioManager.setMusicVolume(this.settings.musicEnabled ? this.settings.musicVolume / 100 : 0);
  }
  
  /**
   * Get effective volume for a sound based on its category
   */
  private getEffectiveVolume(soundKey: string, volumeMultiplier: number): number {
    if (!this.settings.masterEnabled) return 0;
    
    const category = SOUND_CATEGORIES[soundKey] || AudioCategory.SFX;
    let categoryVolume = 1;
    let categoryEnabled = true;
    
    switch (category) {
      case AudioCategory.Music:
        categoryVolume = this.settings.musicVolume / 100;
        categoryEnabled = this.settings.musicEnabled;
        break;
      case AudioCategory.SFX:
        categoryVolume = this.settings.sfxVolume / 100;
        categoryEnabled = this.settings.sfxEnabled;
        break;
      case AudioCategory.UI:
        categoryVolume = this.settings.uiVolume / 100;
        categoryEnabled = this.settings.uiEnabled;
        break;
      case AudioCategory.Voice:
        categoryVolume = this.settings.voiceVolume / 100;
        categoryEnabled = this.settings.voiceEnabled;
        break;
    }
    
    if (!categoryEnabled) return 0;
    
    const baseVolume = SOUND_VOLUMES[soundKey] ?? 1;
    const masterVolume = this.settings.masterVolume / 100;
    
    return baseVolume * volumeMultiplier * categoryVolume * masterVolume;
  }
  
  /**
   * Check if a category is enabled
   */
  private isCategoryEnabled(soundKey: string): boolean {
    if (!this.settings.masterEnabled) return false;
    
    const category = SOUND_CATEGORIES[soundKey] || AudioCategory.SFX;
    
    switch (category) {
      case AudioCategory.Music: return this.settings.musicEnabled;
      case AudioCategory.SFX: return this.settings.sfxEnabled;
      case AudioCategory.UI: return this.settings.uiEnabled;
      case AudioCategory.Voice: return this.settings.voiceEnabled;
      default: return true;
    }
  }
  
  /**
   * Initialize audio system
   */
  async init(): Promise<void> {
    await this.audioManager.init();
    this.applySettings();
    console.log('[GameAudio] Initialized');
  }
  
  /**
   * Load all game sounds (call during loading screen)
   */
  async loadAllSounds(): Promise<void> {
    const loadPromises: Promise<void>[] = [];
    
    for (const [key, url] of Object.entries(SOUND_FILES)) {
      loadPromises.push(
        this.audioManager.loadSound(key, url)
          .then(() => {
            this.loadedSounds.add(key);
          })
          .catch(() => {
            console.warn(`[GameAudio] Failed to load: ${key}`);
          })
      );
    }
    
    // Wait for all with timeout
    await Promise.race([
      Promise.allSettled(loadPromises),
      new Promise(resolve => setTimeout(resolve, 10000)),
    ]);
    
    console.log(`[GameAudio] Loaded ${this.loadedSounds.size}/${Object.keys(SOUND_FILES).length} sounds`);
  }
  
  /**
   * Load essential sounds only (for faster startup)
   */
  async loadEssentialSounds(): Promise<void> {
    const essential = [
      // All weapon sounds
      SFX.PulsePistolShoot,
      SFX.PlasmaRifleShoot,
      SFX.ScatterGunShoot,
      SFX.FusionCannonShoot,
      // Player sounds
      SFX.PlayerDamage,
      SFX.PlayerDeath,
      // Combat sounds
      SFX.KillConfirmed,
      // Game sounds
      SFX.RoundStart,
      SFX.CountdownTick,
      SFX.AirplaneFlyby,
      SFX.ParachuteDrop,
      SFX.WeaponPickup,
      SFX.HealthPickup,
      SFX.ItemPickup,
      SFX.RocketLaunch,
      SFX.MissileIncoming,
      // UI sounds
      SFX.UIClick,
      SFX.UIHover,
    ];
    
    for (const key of essential) {
      const url = SOUND_FILES[key];
      if (url) {
        try {
          await this.audioManager.loadSound(key, url);
          this.loadedSounds.add(key);
        } catch {
          // Ignore load errors for optional sounds
        }
      }
    }
    
    console.log(`[GameAudio] Loaded ${this.loadedSounds.size} essential sounds`);
  }
  
  /**
   * Play a sound effect
   */
  play(sound: SFX, volumeMultiplier: number = 1): string | null {
    if (!this.isCategoryEnabled(sound)) return null;
    
    const volume = this.getEffectiveVolume(sound, volumeMultiplier);
    if (volume <= 0) return null;
    
    return this.audioManager.play(sound, { volume });
  }
  
  /**
   * Play a sound effect starting at a specific offset (for syncing)
   */
  playAtOffset(sound: SFX, offsetSeconds: number, volumeMultiplier: number = 1): string | null {
    if (!this.isCategoryEnabled(sound)) return null;
    
    const volume = this.getEffectiveVolume(sound, volumeMultiplier);
    if (volume <= 0) return null;
    
    return this.audioManager.play(sound, { volume, offset: offsetSeconds });
  }
  
  /**
   * Get duration of a sound in seconds
   */
  getSoundDuration(sound: SFX): number {
    return this.audioManager.getSoundDuration(sound);
  }
  
  /**
   * Play a sound with pitch variation (good for footsteps, shots)
   */
  playWithVariation(sound: SFX, volumeMultiplier: number = 1): string | null {
    if (!this.isCategoryEnabled(sound)) return null;
    
    const volume = this.getEffectiveVolume(sound, volumeMultiplier);
    if (volume <= 0) return null;
    
    const pitchVariation = 0.9 + Math.random() * 0.2; // 0.9 - 1.1
    
    return this.audioManager.play(sound, { volume, pitch: pitchVariation });
  }
  
  /**
   * Stop a specific sound
   */
  stop(id: string): void {
    this.audioManager.stop(id);
  }
  
  /**
   * Play background music
   */
  playMusic(music: Music, fadeIn: number = 1): void {
    if (!this.settings.masterEnabled || !this.settings.musicEnabled) return;
    this.audioManager.playMusic(music, fadeIn);
  }
  
  /**
   * Stop background music
   */
  stopMusic(fadeOut: number = 0.5): void {
    this.audioManager.stopMusic(fadeOut);
  }
  
  /**
   * Stop all audio
   */
  stopAll(): void {
    this.audioManager.stopAll();
  }
  
  /**
   * Check if audio is enabled
   */
  isEnabled(): boolean {
    return this.settings.masterEnabled;
  }
  
  /**
   * Resume audio context (call after user interaction)
   */
  async resume(): Promise<void> {
    await this.audioManager.resume();
  }
}

// Singleton instance
let gameAudioInstance: GameAudio | null = null;

export function getGameAudio(): GameAudio {
  if (!gameAudioInstance) {
    gameAudioInstance = new GameAudio();
  }
  return gameAudioInstance;
}
